import { useState, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { HStack, IconButton, Container } from '@chakra-ui/react';
import Tooltip from '@material-ui/core/Tooltip';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { LuCheck, LuMinus } from 'react-icons/lu';
import { Alert, AlertIcon, Button, Text } from 'components/v4';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import { LoadingIndicator } from '@/components/v4';
import useColumnState from '../../../hooks/useColumnState';
import TrunkStatusCellRenderer from './cellRenderer/TrunkStatusCellRenderer';
import TrunkStatuses from './utils/statuses';

import useGetAllTrunks from './v5/queries/useGetAllTrunks';
import useSRTTrunkTypes from 'hooks/useSRTTrunkTypes';
import useGetCompanyServices from './queries/useGetCompanyServices';
import { trunkTypeDisplayName } from '@/constants/trunkTypes';
import { AlertTitle } from '@chakra-ui/react';

const TRUNK_MODES = {
    MAIN_TABLE: 'MainTable',
    LOADING: 'Loading',
};

// Trunks Base page in telephony section
const TrunksIndex = () => {
    const { darkMode } = useSelector((state) => state.settings);
    const gridRef = useRef();
    const navigate = useNavigate();

    const [mode] = useState(TRUNK_MODES.MAIN_TABLE);

    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });

    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    const { isLoading, isRefetching, isFetching, refetch } =
        useGetAllTrunks(setTableData);

    // Checks if the current company has SBC as a service / SRT as a service type when component renders
    const { data: companyServices = [] } = useGetCompanyServices({
        companyID: currentCompany,
    });
    const sbcAsAService = companyServices.some(
        (service) =>
            service.serviceType === 'SBCaaS' || service.serviceType === 'SRT',
    );

    // SRT trunks are matched by trunk type id — the display name is
    // backend-owned and must not be relied on for routing. Only companies with
    // an SRT service have trunk types to fetch.
    const hasSRTService = companyServices.some(
        (service) => service.serviceType === 'SRT',
    );
    const { isSRTTrunk } = useSRTTrunkTypes({ enabled: hasSRTService });

    const handleEdit = useCallback(
        (params) => {
            const editTrunkNavigate = (data) => {
                if (isSRTTrunk(data)) {
                    navigate(`/trunks/edit/srt/${data.id}`);
                    return;
                }
                switch (data.trunkType.name) {
                    case 'MS Direct Routing':
                        navigate(`/trunks/edit/dr/${data.id}`);
                        break;
                    case 'MS Operator Connect':
                        navigate(`/trunks/edit/oc/${data.id}`);
                        break;
                    case 'SIP Trunk':
                        navigate(`/trunks/edit/sip/${data.id}`);
                        break;
                    case 'Carrier - External':
                        navigate(`/trunks/edit/ext/${data.id}`);
                        break;
                    case 'Zoom Provider Exchange':
                        navigate(`/trunks/edit/zoom/${data.id}`);
                        break;
                    case '8x8':
                        navigate(`/trunks/edit/8x8/${data.id}`);
                        break;
                    case 'ACS':
                        navigate(`/trunks/edit/acs/${data.id}`);
                        break;
                    case 'Webex':
                        navigate(`/trunks/edit/webex/${data.id}`);
                        break;
                    case 'Operator Platform':
                        navigate(`/trunks/edit/operator-platform/${data.id}`);
                        break;
                    case 'MS Direct Routing GCC High':
                        navigate(`/trunks/edit/GCCH/${data.id}`);
                        break;
                    default:
                        return;
                }
            };
            editTrunkNavigate(params.data);
        },
        [navigate, isSRTTrunk],
    );

    const addTrunkNavigate = () => {
        navigate('/trunks/add');
    };

    const EditableCellRenderer = useCallback((params) => {
        const isEditable = params.data?.trunkType?.allowUserEdit !== false;
        return isEditable ? (
            <LuCheck fontSize="large" />
        ) : (
            <LuMinus fontSize="large" />
        );
    }, []);

    const baseColumns = useMemo(() => {
        return [
            {
                field: 'name',
                headerName: 'Trunk Name',
                flex: 1,
                minWidth: 250,
                sortable: true,
                sort: 'desc',
            },
            {
                field: 'trunkType.name',
                headerName: 'Trunk Type',
                width: 200,
                sortable: true,
                valueGetter: ({ data }) =>
                    trunkTypeDisplayName(data?.trunkType),
            },
            {
                field: 'channels',
                headerName: 'Channels',
                width: 50,
                sortable: true,
            },
            {
                field: 'sipHeader',
                headerName: 'SIP Header',
                width: 300,
                sortable: true,
            },
            {
                field: 'status',
                headerName: 'Status',
                width: 150,
                cellRenderer: (params) => {
                    return (
                        <TrunkStatusCellRenderer
                            value={params.value}
                            statusMessage={params.data?.engineMessage}
                            parent
                        />
                    );
                },
                cellStyle: { display: 'flex', alignItems: 'center' },
                sortable: true,
                filter: true,
                valueFormatter: (params) => {
                    const statusLookup = TrunkStatuses[params?.value];
                    const displayName =
                        statusLookup?.displayedName || 'Unknown';
                    return displayName;
                },
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        const statusLookup = TrunkStatuses[params?.value];
                        const displayName =
                            statusLookup?.displayedName || 'Unknown';
                        if (!isSelectedAll) {
                            return <Text isTruncated>{displayName}</Text>;
                        } else {
                            return '(Select All)';
                        }
                    },
                },
            },
            {
                field: 'trunkType.allowUserEdit',
                headerName: 'Editable',
                width: 50,
                cellRenderer: EditableCellRenderer,
                sortable: false,
                hide: true,
            },
            {
                maxWidth: 100,
                headerName: 'Action',
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                lockPosition: 'right',
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                cellRenderer: (params) => {
                    const canEdit =
                        params.data.status !== 9 &&
                        !(
                            params.data.trunkType?.allowUserEdit === false &&
                            params.data.trunkType?.headerValue === 'sip'
                        );

                    return (
                        <HStack
                            spacing={1}
                            justifyContent="left"
                            alignItems="center"
                            width="100%"
                            height="100%">
                            {canEdit && (
                                <Tooltip title="Edit Trunk">
                                    <IconButton
                                        icon={
                                            <BsThreeDotsVertical
                                                fill={
                                                    darkMode ? 'white' : 'black'
                                                }
                                            />
                                        }
                                        aria-label="Edit"
                                        variant="ghost"
                                        onClick={() => handleEdit(params)}
                                        _hover={{
                                            bg: darkMode
                                                ? 'whiteAlpha.200'
                                                : 'blackAlpha.100',
                                        }}
                                        _active={{
                                            bg: darkMode
                                                ? 'whiteAlpha.300'
                                                : 'blackAlpha.200',
                                        }}
                                        _focus={{ boxShadow: 'none' }}
                                    />
                                </Tooltip>
                            )}
                        </HStack>
                    );
                },
            },
        ];
    }, [EditableCellRenderer, darkMode, handleEdit]);

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const tableDataWithColumns = useMemo(
        () => ({
            columns,
            data: tableData.data,
        }),
        [columns, tableData.data],
    );

    const isFetchingData = isLoading || isRefetching || isFetching;

    if (!currentCompany) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>
                    No Partner / Company / Wholesaler Selected
                </AlertTitle>
                <Text>Please select a wholesaler and a partner / company.</Text>
            </Alert>
        );
    }

    const mainTable = (
        <Container
            as="div"
            maxW="container.xl"
            px={0}
            aria-label="trunks main table">
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    marginBottom: '15px',
                }}>
                Trunks
            </Text>
            <AGGridMain
                gridRef={gridRef}
                tableData={tableDataWithColumns}
                isLoading={isFetchingData}
                refetch={refetch}
                onColumnVisible={onColumnVisible}
                overlayNoRowsTemplate="No Trunks Found"
                titleName="Trunks"
                extraComponents={
                    sbcAsAService && (
                        <div style={{ paddingLeft: '20px' }}>
                            <Button
                                size="sm"
                                width="120px"
                                onClick={addTrunkNavigate}>
                                + Add Trunk
                            </Button>
                        </div>
                    )
                }
            />
        </Container>
    );

    const modalContent = () => {
        switch (mode) {
            case TRUNK_MODES.MAIN_TABLE:
                return mainTable;
            case TRUNK_MODES.LOADING:
                return <LoadingIndicator />;
            default:
                return mainTable;
        }
    };

    return <>{modalContent()}</>;
};

export default TrunksIndex;
